import { useEffect, useRef, useState } from 'react';
import { AlertTriangle, Check, Info, X, XCircle } from 'lucide-react';

import { cn } from '../../lib/cn';
import type { ToastItem, ToastVariant } from './toast-types';
import styles from './Toast.module.css';

const ICON_FOR: Record<ToastVariant, typeof Check> = {
  success: Check,
  error: XCircle,
  warning: AlertTriangle,
  info: Info,
};

interface Props {
  item: ToastItem;
  onDismiss: (id: string) => void;
}

export function Toast({ item, onDismiss }: Props) {
  const [leaving, setLeaving] = useState(false);
  const timerRef = useRef<number | undefined>(undefined);
  const Icon = ICON_FOR[item.variant];

  function startDismiss() {
    setLeaving(true);
    window.setTimeout(() => { onDismiss(item.id); }, 120);
  }

  useEffect(() => {
    timerRef.current = window.setTimeout(() => { startDismiss(); }, item.duration);
    return () => {
      if (timerRef.current !== undefined) window.clearTimeout(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.id, item.duration]);

  function pause() {
    if (timerRef.current !== undefined) {
      window.clearTimeout(timerRef.current);
      timerRef.current = undefined;
    }
  }

  function resume() {
    if (leaving || timerRef.current !== undefined) return;
    timerRef.current = window.setTimeout(() => { startDismiss(); }, item.duration);
  }

  return (
    <div
      className={cn(styles.toast, styles[item.variant], leaving && styles.leaving)}
      role={item.variant === 'error' ? 'alert' : 'status'}
      onMouseEnter={pause}
      onMouseLeave={resume}
      onFocus={pause}
      onBlur={resume}
    >
      <Icon size={18} className={styles.icon} aria-hidden="true" />
      <div className={styles.body}>
        <p className={styles.message}>{item.message}</p>
        {item.action ? (
          <button
            type="button"
            className={styles.action}
            onClick={() => {
              item.action?.onClick();
              startDismiss();
            }}
          >
            {item.action.label}
          </button>
        ) : null}
      </div>
      <button
        type="button"
        className={styles.close}
        onClick={startDismiss}
        aria-label="סגור"
      >
        <X size={14} aria-hidden="true" />
      </button>
      <span
        className={styles.progress}
        style={{ animationDuration: `${String(item.duration)}ms` }}
        aria-hidden="true"
      />
    </div>
  );
}
